// After every exchange a light model reads the two messages and writes down what is worth keeping. This is
// where "it knows her" comes from, and where "honesty can come later" is implemented: a view it held back in
// a hot moment becomes an open follow-up for a calmer day.
import type { ChatMessage } from '@/llm/types';
import { dateStamp } from '@/prompt/assemble';
import { MEMORY_KINDS, type Memory, type MemoryKind, type ProfileTopic } from '@/types';
import { TOPIC_IDS } from './curiosity';

export interface ExtractionAdd {
  kind: MemoryKind;
  text: string;
  subjects: string[];
  importance: 1 | 2 | 3 | 4 | 5;
  happenedAt?: string;
  topic?: ProfileTopic;
}

export interface ExtractionUpdate {
  id: string;
  text?: string;
  subjects?: string[];
  importance?: 1 | 2 | 3 | 4 | 5;
  status?: 'open' | 'done';
  happenedAt?: string;
  topic?: ProfileTopic;
}

export interface Extraction {
  add: ExtractionAdd[];
  update: ExtractionUpdate[];
  journal: string;
  /** Picture topics the companion asked her about this turn, and ones she deflected. */
  asked: ProfileTopic[];
  declined: ProfileTopic[];
}

export const EXTRACTION_SYSTEM = `你是一个安静的记录者。你读一段对话（她和她的伴侣 AI 之间的），把值得长期记住的东西写成一条条笔记。只输出 JSON。

笔记的种类（kind）：
- about_me：关于她本人的稳定事实——工作、家庭、性格、喜欢什么、忌讳什么。importance 高。
- person：她生活里的人是谁——关系、角色、她对这个人的基本态度。一个人只有一条，有了就更新，不要再加。
- event：发生了的事。带 happenedAt（YYYY-MM-DD）。
- feeling：和某件事绑在一起的、值得记的情绪状态。不记日常的小情绪。
- preference：她的偏好、习惯、雷区。
- belief：她的价值观和看法。
- prediction：她对未来的判断（「他撑不过三个月」「这事最后肯定是我背锅」）。subjects 里写对象。happenedAt 是她说这话的日子。日后应验或落空时，把那条 status 改成 done，text 里补一句结果。
- followup：两种情况才写：(1) 她说了要做的事、要发生的事，值得之后问一句；(2) 伴侣在她情绪很热的时候压下了没说的看法，等她冷静了该提——写成「等她冷静了，提一下……」。

每条笔记还要标一个 topic——它填的是她画像的哪一块：
people（她生活里的人是谁）、family（她的家：孩子的年龄性格喜好、家里的分工与周末、一起去过的地方）、comfort（不开心时希望别人怎么做）、triggers（最不想听到的话）、stress（压力来自哪里）、work（工作与节奏）、ongoing（最近在忙或操心的事）、daily（日常作息）、hobbies（她自己喜欢做什么）、health（身体状况）、past（过去的经历）。都不沾边就不写 topic。

另外判断这一轮里两件事：
- asked：伴侣另起话头问了她本人或家人哪方面的情况（用上面的 topic 名）。顺着她正在说的事追问细节（「他几岁了」「后来呢」）不算，只算把话题引向她画像的那种。
- declined：她对伴侣的哪个问题没有正面回答、岔开了、或者说不想说。

规则：
- text 一句话，第三人称，用她的名字。相对时间（昨天、上周、下个月）一律换算成绝对日期，今天的日期在下面给你。
- subjects 用规范的称呼（婆婆、小王、老板、公司），和已有笔记里的写法保持一致。
- 一件事只记一条。已有相关笔记时用 update 改它，不要重复 add。
- 不记闲聊，不记伴侣自己说的话（followup 除外）。宁缺毋滥：一轮对话通常只有 0 到 3 条。
- journal：把「今天」这一行日记重写成完整的一两句——她今天聊了什么、状态怎么样。已有的话在它基础上改，不是追加。没有值得写的就返回空字符串。

输出格式（只输出这个 JSON，不要别的）：
{"add":[{"kind":"event","text":"…","subjects":["…"],"topic":"work","importance":3,"happenedAt":"YYYY-MM-DD"}],"update":[{"id":"…","text":"…","status":"done"}],"journal":"…","asked":["people"],"declined":[]}`;

export interface ExtractionInput {
  userName: string;
  companionName: string;
  now: number;
  /** The exchange to read: her message and its reply, plus a little earlier context. */
  exchange: { role: 'user' | 'assistant'; text: string }[];
  related: Memory[];
  todayJournal?: string;
}

/** Pure; exported for tests. */
export function buildExtractionMessages(input: ExtractionInput): ChatMessage[] {
  const who = `她叫${input.userName || '她'}，伴侣叫${input.companionName || '它'}。今天是 ${dateStamp(input.now)}。`;
  const relatedBlock = input.related.length
    ? `已有的相关笔记（要改就用 update 带上 id）：\n${input.related.map((m) => `- id=${m.id} [${m.kind}${m.status ? `,${m.status}` : ''}] ${m.text} (subjects: ${m.subjects.join('、') || '无'})`).join('\n')}`
    : '已有的相关笔记：无。';
  const journalBlock = input.todayJournal ? `今天已有的日记：${input.todayJournal}` : '今天还没有日记。';
  const convo = input.exchange.map((m) => `${m.role === 'user' ? input.userName || '她' : input.companionName || '它'}：${m.text}`).join('\n\n');
  return [{ role: 'user', content: [{ type: 'text', text: `${who}\n\n${relatedBlock}\n\n${journalBlock}\n\n对话：\n${convo}` }] }];
}

function clampImportance(v: unknown): 1 | 2 | 3 | 4 | 5 {
  const n = Math.round(Number(v));
  return (Number.isFinite(n) ? Math.min(5, Math.max(1, n)) : 3) as 1 | 2 | 3 | 4 | 5;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Pure; exported for tests. Tolerates code fences and prose around the JSON; drops malformed entries. */
export function parseExtraction(raw: string): Extraction {
  const empty: Extraction = { add: [], update: [], journal: '', asked: [], declined: [] };
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start < 0 || end <= start) return empty;
  let json: unknown;
  try {
    json = JSON.parse(raw.slice(start, end + 1));
  } catch {
    return empty;
  }
  if (!json || typeof json !== 'object') return empty;
  const o = json as Record<string, unknown>;
  const add: ExtractionAdd[] = [];
  for (const a of Array.isArray(o.add) ? o.add : []) {
    const r = a as Record<string, unknown>;
    if (typeof r.text !== 'string' || !r.text.trim() || !MEMORY_KINDS.includes(r.kind as MemoryKind)) continue;
    add.push({
      kind: r.kind as MemoryKind,
      text: r.text.trim(),
      subjects: Array.isArray(r.subjects) ? r.subjects.filter((s): s is string => typeof s === 'string' && !!s.trim()).map((s) => s.trim()) : [],
      importance: clampImportance(r.importance),
      ...(typeof r.happenedAt === 'string' && DATE.test(r.happenedAt) ? { happenedAt: r.happenedAt } : {}),
      ...(isTopic(r.topic) ? { topic: r.topic } : {}),
    });
  }
  const update: ExtractionUpdate[] = [];
  for (const u of Array.isArray(o.update) ? o.update : []) {
    const r = u as Record<string, unknown>;
    if (typeof r.id !== 'string' || !r.id) continue;
    const patch: ExtractionUpdate = { id: r.id };
    if (typeof r.text === 'string' && r.text.trim()) patch.text = r.text.trim();
    if (Array.isArray(r.subjects)) patch.subjects = r.subjects.filter((s): s is string => typeof s === 'string' && !!s.trim()).map((s) => s.trim());
    if (r.importance !== undefined) patch.importance = clampImportance(r.importance);
    if (r.status === 'open' || r.status === 'done') patch.status = r.status;
    if (typeof r.happenedAt === 'string' && DATE.test(r.happenedAt)) patch.happenedAt = r.happenedAt;
    if (isTopic(r.topic)) patch.topic = r.topic;
    update.push(patch);
  }
  const topics = (v: unknown): ProfileTopic[] => (Array.isArray(v) ? v.filter(isTopic) : []);
  return { add, update, journal: typeof o.journal === 'string' ? o.journal.trim() : '', asked: topics(o.asked), declined: topics(o.declined) };
}

function isTopic(v: unknown): v is ProfileTopic {
  return typeof v === 'string' && (TOPIC_IDS as string[]).includes(v);
}

/** Pure; exported for tests. New and changed notes to store; updates for unknown ids are ignored. */
export function applyExtraction(existing: Memory[], ex: Extraction, sourceIds: string[], now: number, newId: () => string): Memory[] {
  const byId = new Map(existing.map((m) => [m.id, m]));
  const out: Memory[] = [];
  for (const u of ex.update) {
    const m = byId.get(u.id);
    if (!m) continue;
    out.push({
      ...m,
      ...(u.text ? { text: u.text } : {}),
      ...(u.subjects ? { subjects: u.subjects } : {}),
      ...(u.importance ? { importance: u.importance } : {}),
      ...(u.status ? { status: u.status } : {}),
      ...(u.happenedAt ? { happenedAt: u.happenedAt } : {}),
      ...(u.topic ? { topic: u.topic } : {}),
      sourceIds: Array.from(new Set([...m.sourceIds, ...sourceIds])),
      updatedAt: now,
    });
  }
  for (const a of ex.add) {
    // A person note is one per person: fold into the existing one when the model added instead of updating.
    const dup = a.kind === 'person' && existing.find((m) => m.kind === 'person' && m.subjects.some((s) => a.subjects.includes(s)));
    if (dup && !out.some((m) => m.id === dup.id)) {
      out.push({ ...dup, text: a.text, subjects: Array.from(new Set([...dup.subjects, ...a.subjects])), importance: Math.max(dup.importance, a.importance) as Memory['importance'], topic: a.topic ?? dup.topic ?? 'people', sourceIds: Array.from(new Set([...dup.sourceIds, ...sourceIds])), updatedAt: now });
      continue;
    }
    out.push({
      id: newId(),
      kind: a.kind,
      text: a.text,
      subjects: a.subjects,
      importance: a.importance,
      createdAt: now,
      updatedAt: now,
      sourceIds,
      ...(a.happenedAt ? { happenedAt: a.happenedAt } : {}),
      ...(a.topic ? { topic: a.topic } : a.kind === 'person' ? { topic: 'people' as const } : {}),
      ...(a.kind === 'followup' || a.kind === 'prediction' ? { status: 'open' as const } : {}),
    });
  }
  return out;
}
